/**
 * Strategy's dialogs on the dialog anatomy (Figma 13: Go live 72:5703 /
 * 72:5784, Stop sharing 72:7038): Barlow title, a lead, fact rows, choices,
 * then the footer. Only what the backend does is said: approvals are not built
 * yet (phase 5), and going live is always now.
 */
import { useState, type ReactNode } from "react";
import { Radio, RadioGroup } from "react-aria-components";
import { cn } from "@/utils/helpers";
import type { BrandCalendarSettings } from "@/types/contentCalendar.type";
import type { StrategyDocument, StrategyListItem } from "@/types/strategy.type";
import { KitButton } from "../../components/kit/Button";
import { KitModal } from "../../components/kit/Modal";
import { dayMonth } from "./deckModel";

/** Dialog / Fact row: label 160, value. */
function FactRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex w-full flex-col gap-1 border-t border-co-line py-3 sm:flex-row sm:gap-4">
      <dt className="shrink-0 text-[13px] text-co-muted sm:w-[150px]">{label}</dt>
      <dd className="min-w-0 flex-1 text-[14px] font-medium leading-[1.45] text-co-text">{children}</dd>
    </div>
  );
}

/** A section label in a dialog ("VOLUME"). */
function DialogLabel({ children }: { children: ReactNode }) {
  return <p className="pt-3 font-section text-[13px] font-semibold uppercase tracking-[1.2px] text-co-muted">{children}</p>;
}

/** Settings / Choice: a radio row with its line of what it means; the chosen row is tinted. */
function Choice({ value, title, note }: { value: string; title: string; note?: string }) {
  return (
    <Radio
      value={value}
      className={({ isSelected, isFocusVisible }) =>
        cn(
          "flex cursor-pointer items-start gap-3 rounded-card px-4 py-3 outline-none [transition:background-color_var(--spring-snappy)]",
          isSelected ? "bg-co-accent-dim" : "hover:bg-co-hover",
          isFocusVisible && "outline-2 outline-offset-2 outline-co-accent",
        )
      }
    >
      {({ isSelected }) => (
        <>
          <span
            aria-hidden
            className={cn(
              "mt-0.5 grid size-[18px] shrink-0 place-items-center rounded-full border-[1.5px] transition-colors duration-[160ms]",
              isSelected ? "border-co-accent" : "border-co-faint",
            )}
          >
            {isSelected ? <span className="co-dot-in size-2 rounded-full bg-co-accent" /> : null}
          </span>
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="text-[14px] font-semibold text-co-text">{title}</span>
            {note ? <span className="text-[13px] leading-[1.45] text-co-muted">{note}</span> : null}
          </span>
        </>
      )}
    </Radio>
  );
}

/** The strategy's monthly volume in the calendar's three counts (backend strategyCalendarScope). */
export function strategyVolume(doc: StrategyDocument | null | undefined) {
  const mix = doc?.content?.volume?.calendar_mix;
  if (!mix) return null;
  const vertical = Math.max(0, Math.round(mix.vertical_video_posts));
  const statics = Math.max(0, Math.round(mix.static_posts));
  const horizontal = Math.max(0, Math.round(mix.horizontal_video_posts));
  const total = vertical + statics + horizontal;
  if (total < 1 || total > 30) return null;
  const parts = [
    vertical ? `${vertical} vertical video` : "",
    statics ? `${statics} static` : "",
    horizontal ? `${horizontal} horizontal` : "",
  ].filter(Boolean);
  return { total, parts: parts.join(", ") };
}

const calendarTotal = (settings: BrandCalendarSettings) =>
  settings.staticPostsPerMonth + settings.verticalVideoPostsPerMonth + settings.horizontalVideoPostsPerMonth;

/**
 * Make it the live strategy (12 · Going live 02 / 03). Says what changes in
 * the calendar: its pillars, its volume (or, when a person set the calendar's
 * volume by hand, the choice between theirs and the strategy's), and which
 * strategy it replaces. Going live is always now: waiting for a date has no
 * backend.
 */
export function GoLiveDialog({
  strategy,
  doc,
  replaces,
  calendar,
  onConfirm,
  onClose,
}: {
  strategy: StrategyListItem;
  doc: StrategyDocument | null;
  /** The strategy live now, which going live archives. */
  replaces: StrategyListItem | null;
  calendar: BrandCalendarSettings | null;
  onConfirm: (useStrategyVolume: boolean) => void;
  onClose: () => void;
}) {
  const pillars = doc?.content?.pillars.map((p) => p.name).filter(Boolean) ?? [];
  const volume = strategyVolume(doc);
  const handSet = !!volume && calendar?.scopeSource === "PERSON";
  const [volumeChoice, setVolumeChoice] = useState<"keep" | "strategy">("keep");
  const version = /^v(\d+)$/.exec(strategy.version)?.[1];
  const list = (names: string[]) => (names.length < 2 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`);
  return (
    <KitModal
      isOpen
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={version ? `Make version ${version} the live strategy?` : "Make this the live strategy?"}
      description="Going live changes the calendar:"
      modalClassName="!w-[min(calc(100vw-2rem),600px)] !max-w-[600px]"
    >
      <dl className="flex w-full flex-col">
        {pillars.length ? <FactRow label="Pillars">The calendar plans against {list(pillars)}</FactRow> : null}
        {volume && !handSet ? <FactRow label="Volume">{volume.total} posts a month{volume.parts ? `: ${volume.parts}` : ""}</FactRow> : null}
        {replaces && replaces.id !== strategy.id ? (
          <FactRow label="Replaces">“{replaces.title}” is archived and stays readable</FactRow>
        ) : null}
      </dl>
      {handSet && calendar && volume ? (
        <>
          <DialogLabel>Volume</DialogLabel>
          <RadioGroup aria-label="Volume" value={volumeChoice} onChange={(v) => setVolumeChoice(v as "keep" | "strategy")} className="mt-2 flex flex-col gap-1">
            <Choice
              value="keep"
              title={`Keep the calendar’s ${calendarTotal(calendar)} posts a month`}
              note={`Set by hand${calendar.confirmedAt ? ` on ${dayMonth(calendar.confirmedAt)}` : ""}. The strategy’s ${volume.total} stays as a suggestion in Calendar settings.`}
            />
            <Choice value="strategy" title={`Use the strategy’s ${volume.total} posts a month`} note={volume.parts ? `${volume.parts[0].toUpperCase()}${volume.parts.slice(1)}.` : undefined} />
          </RadioGroup>
        </>
      ) : null}
      <div className="flex items-center justify-end gap-3 pt-5">
        <KitButton variant="ghost" onClick={onClose}>Not yet</KitButton>
        <KitButton onClick={() => onConfirm(handSet && volumeChoice === "strategy")}>Go live</KitButton>
      </div>
    </KitModal>
  );
}

/** A plain confirm (14 · 02 Stop sharing with Huda): the question, one line, two buttons. */
export function SimpleConfirm({
  title,
  body,
  cancelLabel,
  confirmLabel,
  danger,
  onConfirm,
  onClose,
}: {
  title: string;
  body: string;
  cancelLabel: string;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <KitModal
      isOpen
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={title}
      modalClassName="!w-[min(calc(100vw-2rem),600px)] !max-w-[600px]"
    >
      <p className="text-[14px] leading-[1.5] text-co-text2">{body}</p>
      <div className="flex items-center justify-end gap-3 pt-5">
        <KitButton variant="ghost" onClick={onClose}>{cancelLabel}</KitButton>
        <KitButton variant={danger ? "danger" : "primary"} onClick={onConfirm}>{confirmLabel}</KitButton>
      </div>
    </KitModal>
  );
}

/** The go-live checks still open (schema 3.2 §10) and the open values, before the deck is presented or sent. */
export function ReviewNotesDialog({ notes, onClose }: { notes: string[]; onClose: () => void }) {
  return (
    <KitModal
      isOpen
      onOpenChange={(open) => (open ? undefined : onClose())}
      title="Before you send"
      description="What is still open in this version. Only the first lines marked Missing stop it going live."
      modalClassName="!w-[min(calc(100vw-2rem),600px)] !max-w-[600px]"
    >
      <ul className="flex flex-col">
        {notes.map((note) => (
          <li key={note} className="border-t border-co-line py-3 text-[14px] leading-[1.5] text-co-text2">{note}</li>
        ))}
      </ul>
      <div className="flex justify-end pt-4">
        <KitButton onClick={onClose}>Done</KitButton>
      </div>
    </KitModal>
  );
}
